import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Tooltip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
} from '@mui/material';
import {
  FloppyDisk as SaveIcon,
  User as PersonIcon,
  Envelope as EmailIcon,
  Phone as PhoneIcon,
  House as HomeIcon,
  Question as HelpIcon,
  ArrowLeft,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { getAvatarSrc } from '../../utils/avatarConstants';
import { useAuth } from '../../context/AuthContext';
import useThemeStyles from '../../theme/useThemeStyles';
import api from '../../utils/api';

function BasicInformation({ title = 'Basic Information', embedded = false }) {
  const styles = useThemeStyles();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [weddingUsers, setWeddingUsers] = useState([]);
  const [formData, setFormData] = useState({
    partnerOne: {
      userId: null,
      fullName: '',
      preferredName: '',
      pronouns: '',
      email: '',
      phone: '',
      address: '',
      profilePicture: ''
    },
    partnerTwo: {
      userId: null,
      fullName: '',
      preferredName: '',
      pronouns: '',
      email: '',
      phone: '',
      address: '',
      profilePicture: ''
    }
  });

  useEffect(() => {
    fetchPartnerDetails();
  }, []);

  const fetchPartnerDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/wedding-details/partners');
      if (response.data) {
        setWeddingUsers(response.data.weddingUsers || []);
        setFormData(prev => ({
          partnerOne: {
            ...prev.partnerOne,
            ...response.data.partnerOne,
            userId: response.data.partnerOne?.userId || null
          },
          partnerTwo: {
            ...prev.partnerTwo,
            ...response.data.partnerTwo,
            userId: response.data.partnerTwo?.userId || null
          }
        }));
      }
    } catch (err) {
      setError('Failed to load partner details');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (partner, field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [partner]: {
        ...prev[partner],
        [field]: value
      }
    }));
  };

  const getPartnerSource = (partner) => {
    const uid = formData[partner]?.userId;
    if (!uid) return 'manual';
    return uid;
  };

  const handleSourceChange = async (partner, newSource) => {
    const newUserId = newSource === 'me' ? authUser?._id || authUser?.id : (newSource === 'manual' ? null : newSource);
    const updates = { ...formData[partner], userId: newUserId };
    if (newSource === 'me' && authUser) {
      try {
        const res = await api.get('/api/users/profile');
        const p = res.data;
        updates.fullName = [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
        updates.preferredName = p.preferredName || '';
        updates.email = p.email || '';
        updates.phone = p.phone || '';
        updates.address = p.address || '';
        updates.pronouns = p.pronouns || '';
        updates.profilePicture = p.profilePicture || '';
        updates.preferredContactMethod = p.preferredContactMethod || '';
        updates.bestTimeToContact = p.bestTimeToContact || '';
        updates.communicationNotes = p.communicationNotes || '';
      } catch {
        updates.fullName = [authUser.firstName, authUser.lastName].filter(Boolean).join(' ').trim();
        updates.preferredName = authUser.preferredName || '';
        updates.email = authUser.email || '';
        updates.phone = authUser.phone || '';
        updates.address = authUser.address || '';
        updates.pronouns = authUser.pronouns || '';
        updates.profilePicture = authUser.profilePicture || '';
        updates.preferredContactMethod = authUser.preferredContactMethod || '';
        updates.bestTimeToContact = authUser.bestTimeToContact || '';
        updates.communicationNotes = authUser.communicationNotes || '';
      }
    } else if (newSource === 'manual') {
      updates.fullName = updates.fullName || '';
      updates.preferredName = updates.preferredName || '';
      updates.email = updates.email || '';
      updates.phone = updates.phone || '';
      updates.address = updates.address || '';
      updates.pronouns = updates.pronouns || '';
    } else if (typeof newSource === 'string' && newSource.length > 10) {
      const wu = weddingUsers.find(u => (u._id || u.id) === newSource);
      if (wu) {
        updates.fullName = [wu.firstName, wu.lastName].filter(Boolean).join(' ').trim();
        updates.preferredName = wu.preferredName || '';
        updates.email = wu.email || '';
        updates.phone = wu.phone || '';
        updates.address = wu.address || '';
        updates.pronouns = wu.pronouns || '';
        updates.profilePicture = wu.profilePicture || '';
        updates.preferredContactMethod = wu.preferredContactMethod || '';
        updates.bestTimeToContact = wu.bestTimeToContact || '';
        updates.communicationNotes = wu.communicationNotes || '';
      }
    }
    setFormData(prev => ({ ...prev, [partner]: updates }));
  };

  const isProfileLinked = (partner) => {
    const src = getPartnerSource(partner);
    return src === 'me' || (typeof src === 'string' && src.length > 10);
  };

  const handleImageUpload = async (partner, event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setError('Only PNG and JPEG image files are allowed.');
      event.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      event.target.value = '';
      return;
    }

    try {
      setSaving(true);
      setError('');
      const fd = new FormData();
      fd.append('partnerImage', file);
      fd.append('partnerType', partner);
      const response = await api.post('/api/wedding-details/upload-partner-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({
        ...prev,
        [partner]: { ...prev[partner], profilePicture: response.data.imageUrl }
      }));
      setSuccess('Profile picture uploaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload profile picture');
      event.target.value = '';
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await api.put('/api/wedding-details/partners', {
        partners: { partnerOne: formData.partnerOne, partnerTwo: formData.partnerTwo }
      });
      setSuccess('Basic information saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save partner details');
    } finally {
      setSaving(false);
    }
  };

  const getPartnerInitials = (partner) => {
    const data = formData[partner];
    if (!data?.fullName) return '?';
    return data.fullName.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const renderPartnerSection = (partner, label, avatarColor, isPartnerOne = false) => {
    const data = formData[partner];
    const linked = isPartnerOne || isProfileLinked(partner);

    return (
      <Card key={partner} sx={{ ...styles.card.default, mb: 2 }}>
        <CardContent sx={styles.spacing.coupleProfileCard}>
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
            {label}
          </Typography>

          {!isPartnerOne && (
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <InputLabel>Who is this?</InputLabel>
              <Select
                value={getPartnerSource(partner)}
                label="Who is this?"
                onChange={(e) => handleSourceChange(partner, e.target.value)}
              >
                {weddingUsers
                  .filter(wu => (wu._id || wu.id) !== (formData.partnerOne?.userId || ''))
                  .map(wu => (
                    <MenuItem key={wu._id} value={wu._id}>
                      {[wu.firstName, wu.lastName].filter(Boolean).join(' ') || wu.email}
                    </MenuItem>
                  ))}
                <MenuItem value="manual">I'll add their details manually</MenuItem>
              </Select>
            </FormControl>
          )}

          {linked ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'action.hover',
                mb: 1.5
              }}
            >
              <Avatar
                src={getAvatarSrc(data.profilePicture)}
                sx={{ width: 48, height: 48, bgcolor: avatarColor }}
              >
                {getPartnerInitials(partner)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {data.preferredName || data.fullName || 'No name'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Edit in{' '}
                  <Link component="button" variant="body2" onClick={() => navigate('/profile')} sx={{ cursor: 'pointer', fontWeight: 600 }}>
                    Settings → Profile
                  </Link>
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Avatar
                  src={getAvatarSrc(data.profilePicture)}
                  sx={{ width: 56, height: 56, bgcolor: avatarColor }}
                >
                  {getPartnerInitials(partner)}
                </Avatar>
                <Box>
                  <input
                    accept="image/png,image/jpeg,image/jpg"
                    style={{ display: 'none' }}
                    id={`profile-upload-${partner}`}
                    type="file"
                    onChange={(e) => handleImageUpload(partner, e)}
                  />
                  <label htmlFor={`profile-upload-${partner}`}>
                    <Button variant="outlined" size="small" component="span">
                      Upload photo
                    </Button>
                  </label>
                </Box>
              </Box>
              <Grid container spacing={2} sx={{ mb: 1.5 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={data.fullName}
                    onChange={handleChange(partner, 'fullName')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon size={20} />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={data.email}
                    onChange={handleChange(partner, 'email')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon size={20} />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={data.phone}
                    onChange={handleChange(partner, 'phone')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon size={20} />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={data.address}
                    onChange={handleChange(partner, 'address')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HomeIcon size={20} />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Pronouns"
                    value={data.pronouns}
                    onChange={handleChange(partner, 'pronouns')}
                    helperText="Optional"
                    InputProps={{
                      endAdornment: (
                        <Tooltip title="Helps suppliers address you correctly">
                          <IconButton size="small">
                            <HelpIcon size={16} />
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={styles.patterns.loadingState}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: embedded ? 0 : 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: embedded ? 1.5 : 2, px: 2 }}>
        {!embedded && (
          <IconButton onClick={() => navigate('/couple-details')} aria-label="Back to couple details">
            <ArrowLeft size={24} />
          </IconButton>
        )}
        <Typography variant="h5" sx={{ fontWeight: 600 }}>{title}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 2 }}>
        Tell suppliers the essential details they need to know about you both
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {renderPartnerSection('partnerOne', 'Partner 1', '#E91E63', true)}
      {renderPartnerSection('partnerTwo', 'Partner 2', '#2196F3', false)}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSubmit}
          disabled={saving}
          sx={{ minWidth: 140 }}
        >
          {saving ? 'Saving...' : 'Save Details'}
        </Button>
      </Box>
    </Box>
  );
}

export default BasicInformation;
